// ============================================
// Hemologix Seed Data: Safety Rules (~40 rules)
// ============================================

import type { SafetyRule } from '../../types/formulary';

export const SEED_SAFETY_RULES: SafetyRule[] = [
  // ============================================================
  // BLOCK RULES — Hard stops (severity: 'block')
  // ============================================================

  // --- 1. Warfarin + Omega-3 Fish Oil ---
  {
    id: 'safety-warfarin-omega3',
    name: 'Warfarin + Omega-3 Bleeding Risk',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'warfarin', 'coumadin', 'apixaban', 'eliquis', 'rivaroxaban', 'xarelto',
      'dabigatran', 'pradaxa', 'edoxaban', 'savaysa', 'heparin', 'enoxaparin', 'lovenox',
      'clopidogrel', 'plavix', 'aspirin', 'ASA',
    ],
    affectedProductIds: ['supp-omega-3-fish-oil'],
    message: 'Omega-3 fish oil significantly increases bleeding risk when combined with anticoagulants or antiplatelet agents. This combination requires physician approval and monitoring.',
    clinicalNote: 'Omega-3 fatty acids (EPA/DHA) potentiate anticoagulation via inhibition of platelet aggregation and thromboxane A2 synthesis. At doses >3g/day, EPA/DHA have clinically significant antiplatelet effects. With warfarin: increase INR monitoring frequency to weekly. With DOACs: monitor for signs of bleeding (bruising, gum bleeding, dark stool). With antiplatelets: additive bleeding risk, particularly relevant pre-surgery. The REDUCE-IT trial (icosapent ethyl 4g/day) showed increased bleeding events vs placebo.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 2. SSRIs + Methylene Blue ---
  {
    id: 'safety-ssri-methylene-blue',
    name: 'SSRI + Methylene Blue — Serotonin Syndrome',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'fluoxetine', 'prozac', 'sertraline', 'zoloft', 'paroxetine', 'paxil',
      'citalopram', 'celexa', 'escitalopram', 'lexapro',
      'fluvoxamine', 'luvox',
      'venlafaxine', 'effexor', 'duloxetine', 'cymbalta', 'desvenlafaxine', 'pristiq',
    ],
    affectedProductIds: ['methylene-blue'],
    message: 'LIFE-THREATENING: Methylene blue is a potent MAO-A inhibitor. Combining with SSRIs or SNRIs can cause serotonin syndrome, a potentially fatal condition. This combination is absolutely contraindicated.',
    clinicalNote: 'Methylene blue inhibits MAO-A at therapeutic doses, dramatically increasing serotonin levels. Combined with serotonergic medications, this can cause serotonin syndrome (hyperthermia, rigidity, myoclonus, autonomic instability, altered mental status, death). FDA has issued a Drug Safety Communication warning. SSRIs/SNRIs must be discontinued for at least 5 half-lives (fluoxetine: 5-6 weeks) before methylene blue initiation.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 3. Opioids + Naltrexone ---
  {
    id: 'safety-opioid-naltrexone',
    name: 'Opioids + Naltrexone — Precipitated Withdrawal',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'oxycodone', 'hydrocodone', 'morphine', 'codeine', 'fentanyl', 'tramadol',
      'oxycontin', 'vicodin', 'percocet', 'norco', 'dilaudid', 'hydromorphone',
      'methadone', 'buprenorphine', 'suboxone', 'subutex',
    ],
    affectedProductIds: ['rx-low-dose-naltrexone'],
    message: 'Naltrexone (including low-dose) blocks opioid receptors and will cause precipitated withdrawal in patients currently taking opioids. This is a medical emergency. Opioids must be fully cleared before starting naltrexone.',
    clinicalNote: 'Naltrexone is a competitive opioid antagonist. Even low-dose naltrexone (LDN, 1.5-4.5mg) will displace opioid agonists from mu, kappa, and delta receptors, causing acute precipitated withdrawal (vomiting, diarrhea, abdominal cramps, tachycardia, hypertension, anxiety, agitation). Minimum opioid-free interval: 7-10 days for short-acting opioids, 10-14 days for long-acting, 14+ days for methadone.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 4. Breast Cancer + Estradiol ---
  {
    id: 'safety-breast-cancer-estradiol',
    name: 'Breast Cancer History + Estradiol',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['breast cancer', 'breast cancer history', 'er+ breast cancer', 'her2+ breast cancer'],
    affectedProductIds: ['rx-estradiol-patch', 'rx-testosterone-cream-female'],
    message: 'Estradiol is generally contraindicated in patients with a history of breast cancer due to the risk of recurrence. Testosterone cream may also be contraindicated depending on receptor status. Oncologist consultation required.',
    clinicalNote: 'Exogenous estradiol stimulates proliferation of estrogen receptor-positive breast cancer cells. Even ER-negative breast cancer history is a relative contraindication as receptor status can change. If HRT is considered essential for quality of life, oncologist co-management is mandatory. Vaginal estradiol (ultra-low systemic absorption) may be considered case-by-case for genitourinary symptoms.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 5. G6PD Deficiency + Methylene Blue ---
  {
    id: 'safety-g6pd-methylene-blue',
    name: 'G6PD Deficiency + Methylene Blue — Hemolytic Anemia',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['g6pd deficiency', 'glucose-6-phosphate dehydrogenase deficiency'],
    affectedProductIds: ['methylene-blue'],
    message: 'Methylene blue is absolutely contraindicated in G6PD deficiency. It causes severe hemolytic anemia that can be life-threatening. G6PD screening is mandatory before prescribing methylene blue.',
    clinicalNote: 'Methylene blue requires NADPH (generated by G6PD pathway) for its conversion from the oxidized to reduced form. In G6PD-deficient patients, methylene blue cannot be reduced and instead acts as an oxidant, causing Heinz body formation and severe hemolytic anemia. This is paradoxically the opposite of its intended mechanism. G6PD assay MUST be performed before any methylene blue use.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 6. Nitrates + PDE5 Inhibitors ---
  {
    id: 'safety-nitrates-pde5',
    name: 'Nitrates + Tadalafil/Sildenafil — Severe Hypotension',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'nitroglycerin', 'nitro', 'isosorbide mononitrate', 'imdur',
      'isosorbide dinitrate', 'isordil', 'amyl nitrate', 'poppers',
    ],
    affectedProductIds: ['rx-tadalafil', 'rx-sildenafil'],
    message: 'LIFE-THREATENING: PDE5 inhibitors (tadalafil, sildenafil) combined with nitrates cause severe, potentially fatal hypotension. This combination is absolutely contraindicated.',
    clinicalNote: 'PDE5 inhibitors and nitrates both cause vasodilation via the NO-cGMP pathway. Combined, they produce synergistic, profound vasodilation leading to precipitous blood pressure drop (can fall >50mmHg), syncope, myocardial infarction, or death. Tadalafil has a 36-hour duration — nitrates must be withheld for at least 48 hours after last tadalafil dose. In emergency settings where nitrates are needed, this is a critical medication reconciliation item.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 7. Pregnancy + Tretinoin ---
  {
    id: 'safety-pregnancy-tretinoin',
    name: 'Pregnancy + Tretinoin — Category X Teratogen',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnancy', 'pregnant', 'planning pregnancy', 'trying to conceive'],
    affectedProductIds: ['rx-tretinoin'],
    message: 'Tretinoin (all retinoids) is Category X — absolutely contraindicated in pregnancy due to severe birth defects including craniofacial, cardiac, and CNS malformations.',
    clinicalNote: 'Retinoids (tretinoin, isotretinoin, adapalene) are known teratogens causing retinoid embryopathy: microtia, anotia, micrognathia, cleft palate, conotruncal heart defects, thymic aplasia, and CNS defects. Topical tretinoin has lower systemic absorption but remains Category X. Pregnancy testing required before prescribing. Reliable contraception mandatory. Discontinue at least 1 month before conception attempts.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 8. Pregnancy + Finasteride ---
  {
    id: 'safety-pregnancy-finasteride',
    name: 'Pregnancy + Finasteride — Category X',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnancy', 'pregnant', 'planning pregnancy', 'trying to conceive'],
    affectedProductIds: ['rx-finasteride'],
    message: 'Finasteride is Category X — absolutely contraindicated in pregnancy and in women who may become pregnant. Even skin contact with crushed tablets can cause fetal harm (ambiguous genitalia in male fetuses).',
    clinicalNote: 'Finasteride inhibits 5-alpha reductase, blocking DHT production critical for male fetal genital development. Exposure during pregnancy causes hypospadias, ambiguous genitalia, and other urogenital malformations in male fetuses. Women of childbearing potential must not handle crushed/broken tablets. Male patients should use condoms if partner is pregnant or may become pregnant. Finasteride should not be prescribed to women of childbearing potential without reliable contraception.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 9. Pregnancy + Metformin ---
  {
    id: 'safety-pregnancy-metformin',
    name: 'Pregnancy + Metformin — Relative Contraindication',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnancy', 'pregnant'],
    affectedProductIds: ['rx-metformin-er'],
    message: 'Metformin use during pregnancy requires obstetric specialist oversight. While used in gestational diabetes and PCOS, it crosses the placenta and long-term effects on offspring are under study. Our protocol does not cover pregnancy management — refer to OB/GYN.',
    clinicalNote: 'Metformin crosses the placenta and achieves fetal concentrations comparable to maternal levels. While increasingly used in GDM (especially NICE guidelines) and PCOS-related infertility, the MiTy trial raised concerns about neonatal outcomes. Our longevity/optimization protocol context is NOT appropriate for pregnancy management. Refer to maternal-fetal medicine for metformin decisions in pregnancy. Discontinue our Metabolic Rx bundle and Longevity Core upon confirmed pregnancy.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 10. MEN2/MTC + GLP-1 RA ---
  {
    id: 'safety-men2-glp1',
    name: 'MEN2/MTC Family History + Semaglutide/Tirzepatide',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'medullary thyroid carcinoma', 'mtc', 'men2',
      'multiple endocrine neoplasia type 2', 'family history of mtc',
      'family history of medullary thyroid carcinoma',
    ],
    affectedProductIds: ['rx-semaglutide'],
    message: 'GLP-1 receptor agonists (semaglutide, tirzepatide) are contraindicated in patients with personal or family history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN2). FDA black box warning.',
    clinicalNote: 'GLP-1 receptor agonists cause dose-dependent thyroid C-cell tumors in rodents. While causality in humans is not established, the FDA black box warning contraindicates use in patients with personal/family history of MTC or MEN2. Screen for thyroid nodules and family history before prescribing. Calcitonin monitoring is not routinely recommended but consider if thyroid nodule detected.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 11. Hemochromatosis + Iron ---
  {
    id: 'safety-hemochromatosis-iron',
    name: 'Hemochromatosis + Iron Supplementation',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['hemochromatosis', 'iron overload', 'hereditary hemochromatosis', 'hfe mutation'],
    affectedProductIds: ['supp-iron-bisglycinate'],
    message: 'Iron supplementation is absolutely contraindicated in hemochromatosis (iron overload disorder). Excess iron causes organ damage to liver, heart, and pancreas.',
    clinicalNote: 'Hemochromatosis (most commonly HFE C282Y homozygous) causes pathological iron accumulation in parenchymal organs. Additional iron supplementation accelerates organ damage including hepatic fibrosis/cirrhosis, cardiomyopathy, diabetes (pancreatic iron deposition), and arthropathy. Check ferritin and transferrin saturation before prescribing iron to any patient. Ferritin >300 (male) or >200 (female) warrants hemochromatosis workup.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 12. Liver Disease + Metformin ---
  {
    id: 'safety-liver-disease-metformin',
    name: 'Liver Disease + Metformin — Lactic Acidosis Risk',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'cirrhosis', 'hepatic impairment', 'liver failure', 'hepatic failure'],
    affectedProductIds: ['rx-metformin-er'],
    message: 'Metformin is contraindicated in significant liver disease due to impaired lactate clearance and increased risk of lactic acidosis, a rare but potentially fatal condition.',
    clinicalNote: 'The liver is the primary site of lactate metabolism via gluconeogenesis. Hepatic dysfunction impairs lactate clearance, and metformin inhibits hepatic gluconeogenesis (its mechanism of action), compounding lactate accumulation risk. Metformin-associated lactic acidosis (MALA) has a mortality rate of ~50%. ALT/AST >3x ULN or any clinical liver disease is a contraindication. Check hepatic function before prescribing and discontinue if liver disease develops.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 13. Lithium + NSAID ---
  {
    id: 'safety-lithium-nsaid',
    name: 'Lithium + NSAID Interaction',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: ['lithium', 'lithobid', 'eskalith'],
    matchConditions: [],
    affectedProductIds: [],
    message: 'If patient is on lithium, NSAIDs (including OTC ibuprofen, naproxen) can increase lithium levels to toxic range. Ensure all supplement and OTC recommendations avoid NSAID interactions.',
    clinicalNote: 'NSAIDs reduce renal prostaglandin synthesis, decreasing renal blood flow and lithium clearance. This can increase serum lithium levels by 15-50%, pushing therapeutic levels (0.6-1.2 mEq/L) into the toxic range (>1.5 mEq/L). Lithium toxicity causes tremor, ataxia, seizures, renal failure, and death. If any products contain NSAID components or if patient is counseled on OTC pain relief, acetaminophen is the safe alternative. Monitor lithium levels if any medication changes occur.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 14. MAOIs + Methylene Blue ---
  {
    id: 'safety-maoi-methylene-blue',
    name: 'MAOI + Methylene Blue — Serotonin Syndrome',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'phenelzine', 'nardil', 'tranylcypromine', 'parnate',
      'selegiline', 'emsam', 'isocarboxazid', 'marplan',
    ],
    affectedProductIds: ['methylene-blue'],
    message: 'LIFE-THREATENING: MAOIs combined with methylene blue (itself an MAO-A inhibitor) cause serotonin syndrome. This combination is absolutely contraindicated.',
    clinicalNote: 'Methylene blue is a potent reversible MAO-A inhibitor. Combined with irreversible MAOIs (phenelzine, tranylcypromine) or even selective MAO-B inhibitors at higher doses (selegiline), the cumulative MAO inhibition causes catastrophic serotonin accumulation. Serotonin syndrome manifestations: hyperthermia (>41C), muscle rigidity, autonomic instability, seizures, rhabdomyolysis, DIC, death. MAOI washout period: minimum 14 days before methylene blue initiation.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // ============================================================
  // WARNING RULES — Soft alerts (severity: 'warn')
  // ============================================================

  // --- 15. Duplicate Androgen Warning ---
  {
    id: 'safety-duplicate-androgen',
    name: 'Duplicate Androgen Therapy Warning',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-testosterone-cypionate', 'supp-tongkat-ali'],
    affectedProductIds: ['rx-testosterone-cypionate', 'supp-tongkat-ali'],
    message: 'Multiple androgen-boosting agents detected. Testosterone cypionate suppresses endogenous production, making tongkat ali (which boosts endogenous T) ineffective. Choose one approach.',
    clinicalNote: 'Exogenous testosterone (cypionate) suppresses the HPG axis via negative feedback, shutting down endogenous LH/FSH production. Tongkat ali works by enhancing endogenous testosterone production — this pathway is suppressed during TRT. Tongkat ali is appropriate for Tier 1 (pre-TRT) or during PCT, but redundant and ineffective during active TRT. Remove tongkat ali from protocol if testosterone cypionate is prescribed.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 16. Duplicate Estrogen Warning ---
  {
    id: 'safety-duplicate-estrogen',
    name: 'Duplicate Estrogen Therapy Warning',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-estradiol-patch', 'supp-dim'],
    affectedProductIds: ['rx-estradiol-patch', 'supp-dim'],
    message: 'Estradiol (exogenous estrogen) combined with DIM (estrogen metabolism modifier). DIM may alter the intended estradiol levels. Review and adjust dosing.',
    clinicalNote: 'DIM (diindolylmethane) shifts estrogen metabolism toward the 2-hydroxy metabolite pathway, which is generally considered favorable. However, when combined with exogenous estradiol, DIM may reduce the effectiveness of HRT by increasing estradiol clearance. If patient is on Female HRT and previously on the Women\'s Hormone Balance bundle, discontinue DIM and reassess estradiol levels at 6-8 weeks.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 17. Duplicate Insulin Sensitizer Warning ---
  {
    id: 'safety-duplicate-insulin-sensitizer',
    name: 'Duplicate Insulin Sensitizer — Hypoglycemia Risk',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-metformin-er', 'supp-berberine'],
    affectedProductIds: ['rx-metformin-er', 'supp-berberine'],
    message: 'Metformin and berberine both lower blood glucose via similar AMPK pathways. Combined use increases hypoglycemia risk. Reduce berberine dose to 500mg 1x/day when adding metformin.',
    clinicalNote: 'Berberine and metformin share AMPK activation as a primary mechanism. Both independently lower fasting glucose by 15-30 mg/dL and A1C by 0.5-1.0%. Combined, additive glucose lowering increases hypoglycemia risk, particularly in patients with A1C already near target. Protocol: if escalating from Metabolic Reset (Tier 1, berberine only) to Metabolic Rx (Tier 2, metformin), reduce berberine from 1000mg/day to 500mg/day and monitor glucose closely for 2 weeks.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 18. Duplicate 5-Alpha Reductase Inhibitor ---
  {
    id: 'safety-duplicate-5ari',
    name: 'Duplicate 5-Alpha Reductase Inhibitors',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-finasteride', 'supp-saw-palmetto'],
    affectedProductIds: ['rx-finasteride', 'supp-saw-palmetto'],
    message: 'Finasteride and saw palmetto both inhibit 5-alpha reductase. Combined use may cause excessive DHT suppression. Choose one — finasteride (Rx, stronger) or saw palmetto (OTC, milder).',
    clinicalNote: 'Finasteride inhibits type II 5-alpha reductase, reducing DHT by ~70%. Saw palmetto also provides mild 5-alpha reductase inhibition. Combined use may excessively suppress DHT, increasing risk of sexual side effects (decreased libido, erectile dysfunction, reduced ejaculatory volume). If escalating from Prostate Health bundle (Tier 1) to finasteride (Tier 2), discontinue saw palmetto.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 19. Duplicate Anti-Anxiety Warning ---
  {
    id: 'safety-duplicate-anxiolytic',
    name: 'Duplicate Anxiolytic Agents',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['selank', 'rx-buspirone', 'supp-ashwagandha-ksm66'],
    affectedProductIds: ['selank', 'rx-buspirone', 'supp-ashwagandha-ksm66'],
    message: 'Multiple anxiolytic agents detected. While mechanisms differ, combined use may cause excessive sedation or unpredictable interactions. Review necessity of each agent.',
    clinicalNote: 'Selank (GABAergic/serotonergic), buspirone (5-HT1A partial agonist), and ashwagandha (GABAergic, cortisol-lowering) all provide anxiolytic effects through different but overlapping pathways. Additive effects may cause drowsiness, cognitive dulling, or hypotension. Typically: use ashwagandha (Tier 1) first, escalate to buspirone (Tier 2) if insufficient, and consider selank (Tier 3) as alternative. Avoid stacking all three simultaneously.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 20. Duplicate Thyroid Hormone ---
  {
    id: 'safety-duplicate-thyroid',
    name: 'Duplicate Thyroid Support + Thyroid Rx',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-levothyroxine', 'supp-selenium', 'supp-zinc-copper'],
    affectedProductIds: ['rx-levothyroxine', 'supp-selenium'],
    message: 'Thyroid Rx and Thyroid Support supplements detected simultaneously. Selenium/zinc supplements are beneficial with thyroid Rx but must be taken 4 hours apart. Ensure timing separation is clear in protocol.',
    clinicalNote: 'Selenium and zinc support T4-to-T3 conversion and are beneficial adjuncts to levothyroxine. However, mineral supplements (including iron, calcium, and zinc) can chelate levothyroxine in the GI tract, reducing absorption by 30-50%. Protocol: levothyroxine on empty stomach first thing AM, then selenium/zinc with lunch or dinner (minimum 4-hour separation). Patient counseling on timing is critical.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 21. Testosterone — Hematocrit Monitoring ---
  {
    id: 'safety-testosterone-hematocrit',
    name: 'Testosterone Therapy — Hematocrit Monitoring Required',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-testosterone-cypionate'],
    affectedProductIds: ['rx-testosterone-cypionate'],
    message: 'Testosterone therapy increases erythropoiesis. Monitor hematocrit every 3-6 months. If hematocrit exceeds 54%, therapeutic phlebotomy is required and dose reduction should be considered.',
    clinicalNote: 'Testosterone stimulates erythropoietin production, increasing red blood cell mass. Polycythemia (hematocrit >54%) increases blood viscosity and thrombotic risk (DVT, PE, stroke, MI). Monitoring protocol: CBC at baseline, 3 months, 6 months, then q6-12 months. If hematocrit >52%: consider dose reduction or more frequent injections (lower peaks). If >54%: therapeutic phlebotomy (500mL) and mandatory dose adjustment. Subq injections produce more stable levels than IM and may reduce polycythemia incidence.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 22. Statin — Liver Enzyme Monitoring ---
  {
    id: 'safety-statin-liver',
    name: 'Statin Therapy — Liver Enzyme Monitoring',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-rosuvastatin'],
    affectedProductIds: ['rx-rosuvastatin'],
    message: 'Statin therapy requires liver function monitoring. Check ALT at baseline and if symptoms develop (fatigue, jaundice, dark urine). Supplement with CoQ10 (statins deplete CoQ10).',
    clinicalNote: 'Statins can cause hepatotoxicity (ALT elevation >3x ULN in 1-3% of patients). Current guidelines no longer mandate routine periodic liver enzyme monitoring, but baseline ALT is recommended and repeat testing should be performed if hepatotoxic symptoms develop. Additionally, statins inhibit HMG-CoA reductase, which is in the same mevalonate pathway as CoQ10 synthesis. CoQ10 depletion may contribute to statin myopathy. Recommend CoQ10 200mg daily supplementation with any statin.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 23. Rapamycin — Lipid and Glucose Monitoring ---
  {
    id: 'safety-rapamycin-monitoring',
    name: 'Rapamycin — Lipid and Glucose Monitoring Required',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rapamycin'],
    affectedProductIds: ['rapamycin'],
    message: 'Rapamycin commonly elevates LDL, triglycerides, and fasting glucose. Monitor lipid panel and glucose every 3 months during use. Adjust or pause if values become clinically significant.',
    clinicalNote: 'mTOR inhibition with rapamycin causes dyslipidemia in 40-75% of patients (primarily triglyceride and LDL elevation) and hyperglycemia in 10-30%. Mechanism: mTOR inhibition impairs insulin signaling (mTORC2 pathway) and alters hepatic lipid metabolism. Pulsed weekly dosing mitigates but does not eliminate these effects. Monitoring: fasting lipid panel and glucose at baseline, 6 weeks, then q3 months. If LDL rises >30% or triglycerides >300: consider Lipid Management bundle addition or rapamycin dose reduction.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 24. Metformin — B12 Monitoring ---
  {
    id: 'safety-metformin-b12',
    name: 'Metformin — Vitamin B12 Depletion',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-metformin-er'],
    affectedProductIds: ['rx-metformin-er'],
    message: 'Long-term metformin use depletes vitamin B12 in up to 30% of patients. Monitor B12 levels annually and supplement with methylated B-complex.',
    clinicalNote: 'Metformin impairs B12 absorption in the terminal ileum by interfering with the calcium-dependent B12-intrinsic factor complex uptake. B12 deficiency develops in 10-30% of long-term users, potentially causing megaloblastic anemia and peripheral neuropathy (which may be misattributed to diabetic neuropathy). Protocol: check B12 at baseline, then annually. Ensure B-complex is included in any metformin protocol. Consider sublingual B12 if levels remain low despite oral supplementation.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 25. Spironolactone — Potassium Monitoring ---
  {
    id: 'safety-spironolactone-potassium',
    name: 'Spironolactone — Hyperkalemia Risk',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-spironolactone'],
    affectedProductIds: ['rx-spironolactone'],
    message: 'Spironolactone is a potassium-sparing diuretic. Monitor serum potassium at 4 weeks after initiation and every 6 months thereafter. Avoid potassium-rich salt substitutes.',
    clinicalNote: 'Spironolactone blocks aldosterone at the distal tubule, reducing potassium excretion. Hyperkalemia risk increases with renal impairment, ACE inhibitors, ARBs, potassium supplements, and NSAIDs. Check BMP at 1 week and 4 weeks after initiation, then q6 months. K+ >5.5 mEq/L: hold spironolactone and repeat. K+ >6.0: discontinue and evaluate. Also monitor for gynecomastia in males (spironolactone has anti-androgen effects — generally not prescribed to males in our protocol).',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 26. Thyroid Meds + Iron — Timing Separation ---
  {
    id: 'safety-thyroid-iron-timing',
    name: 'Thyroid Medication + Iron — 4-Hour Separation Required',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchProductIds: ['rx-levothyroxine', 'supp-iron-bisglycinate'],
    affectedProductIds: ['rx-levothyroxine', 'supp-iron-bisglycinate'],
    message: 'Iron supplements reduce levothyroxine absorption by 30-50% via chelation in the GI tract. These must be taken at least 4 hours apart. Levothyroxine: AM on empty stomach. Iron: lunch or evening.',
    clinicalNote: 'Ferrous and ferric iron form insoluble complexes with levothyroxine in the stomach, reducing T4 bioavailability by 30-50%. This interaction is well-documented and clinically significant. Protocol timing: levothyroxine immediately upon waking on empty stomach with water only. Iron supplement with lunch or dinner (minimum 4-hour gap). The same applies to calcium and antacids. Patient counseling should emphasize that even "just a few hours" is insufficient — strict 4-hour minimum.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 27. Thyroid Meds + Calcium — Timing Separation ---
  {
    id: 'safety-thyroid-calcium-timing',
    name: 'Thyroid Medication + Calcium — 4-Hour Separation Required',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['calcium', 'calcium carbonate', 'calcium citrate', 'tums', 'antacids'],
    affectedProductIds: ['rx-levothyroxine'],
    message: 'Calcium supplements and antacids reduce levothyroxine absorption. Separate by at least 4 hours. Take levothyroxine on empty stomach AM; calcium with meals later in the day.',
    clinicalNote: 'Calcium forms insoluble complexes with levothyroxine similar to iron. All calcium-containing products (supplements, antacids including Tums, calcium-fortified foods/juices) should be separated by minimum 4 hours from levothyroxine. PPI/H2 blockers also reduce levothyroxine absorption by increasing gastric pH. If patient requires both thyroid support and calcium: strict AM/PM separation in the timing protocol.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 28. Thyroid Meds + Coffee — Absorption Warning ---
  {
    id: 'safety-thyroid-coffee-timing',
    name: 'Thyroid Medication + Coffee — Absorption Interference',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-levothyroxine'],
    affectedProductIds: ['rx-levothyroxine'],
    message: 'Coffee reduces levothyroxine absorption by up to 36%. Wait at least 60 minutes after taking levothyroxine before drinking coffee.',
    clinicalNote: 'Coffee (both caffeinated and decaffeinated) increases intestinal motility and reduces levothyroxine contact time with intestinal absorption sites. Studies show 36% reduction in T4 absorption when coffee is consumed within 30 minutes. Protocol: take levothyroxine immediately upon waking with 8oz water only. Wait minimum 60 minutes before coffee, food, or other supplements. Tirosint (liquid gel cap) is less affected by coffee but not immune.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 29. Iron + Coffee/Tea — Absorption Warning ---
  {
    id: 'safety-iron-tannins',
    name: 'Iron + Coffee/Tea — Absorption Interference',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['supp-iron-bisglycinate'],
    affectedProductIds: ['supp-iron-bisglycinate'],
    message: 'Coffee, tea, and calcium reduce iron absorption by up to 60%. Take iron with vitamin C on an empty stomach, separate from coffee/tea by 2 hours and from calcium by 4 hours.',
    clinicalNote: 'Tannins (in tea), polyphenols (in coffee), and calcium all inhibit non-heme iron absorption. Tannins chelate iron in the GI tract; polyphenols inhibit DMT1 transporter; calcium competes for absorption. Ascorbic acid (vitamin C) reduces ferric to ferrous iron, increasing absorption 3-6x. Protocol: iron + vitamin C on empty stomach (mid-morning or late afternoon), 2+ hours from coffee/tea, 4+ hours from calcium. Iron bisglycinate (chelated) is less affected by these interactions than ferrous sulfate.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 30. Zinc + Copper Balance ---
  {
    id: 'safety-zinc-copper',
    name: 'Zinc Supplementation — Copper Depletion Risk',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['supp-zinc-copper'],
    affectedProductIds: ['supp-zinc-copper'],
    message: 'Long-term zinc supplementation (>25mg/day) depletes copper stores. Ensure copper is included via multivitamin or add 1-2mg copper supplement.',
    clinicalNote: 'Zinc and copper compete for absorption at the intestinal metallothionein transporter. Chronic zinc supplementation >25mg/day without copper supplementation causes copper deficiency, leading to microcytic anemia, neutropenia, and myelopathy (posterior column degeneration mimicking B12 deficiency). Most quality multivitamins include 1-2mg copper. If zinc is prescribed outside a multivitamin-containing protocol, explicitly add copper. Check copper/ceruloplasmin if neurological symptoms develop.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 31. Gender Mismatch: Male Product to Female ---
  {
    id: 'safety-gender-male-to-female',
    name: 'Male-Specific Product Prescribed to Female',
    severity: 'warn',
    triggerType: 'gender',
    matchProductIds: ['rx-testosterone-cypionate', 'rx-finasteride', 'supp-saw-palmetto', 'supp-tongkat-ali'],
    affectedProductIds: ['rx-testosterone-cypionate', 'rx-finasteride', 'supp-saw-palmetto', 'supp-tongkat-ali'],
    message: 'Male-specific product detected in a female patient profile. Testosterone cypionate, finasteride, saw palmetto, and tongkat ali are designed for male physiology. Verify gender and clinical intent before proceeding.',
    clinicalNote: 'These products are formulated for male androgen physiology. Testosterone cypionate in females uses dramatically different dosing (1/10th male dose). Finasteride is Category X in women of childbearing potential. Saw palmetto and tongkat ali are designed for male hormonal profiles. If prescribing testosterone for a female patient, use the female-specific formulation (testosterone-cream-female) at appropriate female dosing.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 32. Gender Mismatch: Female Product to Male ---
  {
    id: 'safety-gender-female-to-male',
    name: 'Female-Specific Product Prescribed to Male',
    severity: 'warn',
    triggerType: 'gender',
    matchProductIds: ['rx-estradiol-patch', 'rx-progesterone-prometrium', 'rx-testosterone-cream-female', 'supp-evening-primrose-oil', 'supp-iron-bisglycinate', 'rx-spironolactone'],
    affectedProductIds: ['rx-estradiol-patch', 'rx-progesterone-prometrium', 'rx-testosterone-cream-female', 'supp-evening-primrose-oil', 'supp-iron-bisglycinate', 'rx-spironolactone'],
    message: 'Female-specific product detected in a male patient profile. Verify gender and clinical intent. Estradiol, progesterone, and spironolactone are not appropriate for male patients in standard protocols.',
    clinicalNote: 'Exogenous estradiol in males causes gynecomastia, reduced testosterone, and increased thrombotic risk. Progesterone has no standard male indication. Spironolactone has anti-androgen effects causing gynecomastia. Iron supplementation in males requires ferritin verification (hemochromatosis is more common in males). The only exception: anastrozole-managed estrogen control as part of Male HRT may warrant monitoring estradiol levels.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 33. Gender Mismatch: Female Iron Bundle to Male ---
  {
    id: 'safety-gender-iron-bundle-male',
    name: 'Iron Recovery (Female) Bundle Assigned to Male',
    severity: 'warn',
    triggerType: 'gender',
    affectedProductIds: ['supp-iron-bisglycinate'],
    message: 'The Iron Recovery bundle is designed for menstruating women. Iron supplementation in males requires thorough evaluation — most male iron deficiency has a GI bleeding source that needs investigation.',
    clinicalNote: 'Iron deficiency in males is uncommon and should raise suspicion for GI blood loss (colon cancer, gastric ulcer, celiac disease), hemolysis, or chronic disease. The Iron Recovery (Female) bundle assumes menstrual blood loss as etiology. For male iron deficiency: investigate etiology before supplementing. Check stool occult blood, consider GI referral. Iron supplementation without etiology investigation may mask serious pathology.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 34. Gender Mismatch: PT-141 to Male ---
  {
    id: 'safety-gender-pt141-male',
    name: 'PT-141 Dosing — Gender Verification',
    severity: 'warn',
    triggerType: 'gender',
    matchProductIds: ['rx-pt141-bremelanotide'],
    affectedProductIds: ['rx-pt141-bremelanotide'],
    message: 'PT-141 (bremelanotide) is FDA-approved for female HSDD only. Off-label male use has limited evidence. Verify gender and clinical justification.',
    clinicalNote: 'Bremelanotide (PT-141) received FDA approval (Vyleesi) specifically for premenopausal female HSDD. Male use is off-label with limited clinical trial data. If prescribing for male sexual dysfunction, tadalafil/sildenafil (PDE5 inhibitors) have far stronger evidence. PT-141 for males may be considered when PDE5 inhibitors are contraindicated or insufficient, but document clinical rationale thoroughly.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 35. Semaglutide — Anesthesia Warning ---
  {
    id: 'safety-glp1-anesthesia',
    name: 'GLP-1 RA — Pre-Surgical/Anesthesia Guidance',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-semaglutide'],
    affectedProductIds: ['rx-semaglutide'],
    message: 'GLP-1 receptor agonists delay gastric emptying, increasing aspiration risk during anesthesia. Discontinue semaglutide/tirzepatide at least 1-2 weeks before any elective surgical procedure requiring anesthesia.',
    clinicalNote: 'GLP-1 RAs delay gastric emptying by 30-50%, increasing retained gastric contents even after standard NPO fasting. ASA 2023 guidelines recommend holding GLP-1 RAs for at least 1 week (weekly formulations) before elective procedures. Some anesthesiologists recommend 2 weeks. Point-of-care gastric ultrasound can assess residual volume. Urgent/emergent procedures: treat as full-stomach, rapid sequence intubation. Include this warning in any GLP-1 prescription counseling.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // ============================================================
  // INFO RULES — Suggestions and optimization notes (severity: 'info')
  // ============================================================

  // --- 36. Foundation Upsell to Metabolic ---
  {
    id: 'safety-upsell-foundation-metabolic',
    name: 'Foundation Bundle — Metabolic Upgrade Opportunity',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['supp-multivitamin', 'supp-berberine'],
    affectedProductIds: [],
    message: 'Patient is on Foundation supplements with early metabolic markers. Consider upgrading to the Metabolic Reset bundle for targeted insulin sensitization with berberine, chromium, and CoQ10.',
    clinicalNote: 'If patient is on Essential Foundation and labs show fasting insulin >7, glucose >95, or A1C >5.3, the Metabolic Reset bundle adds berberine (500mg 2x/day), chromium picolinate (400mcg), CoQ10 (200mg), and green tea extract for comprehensive metabolic support before pharmacological escalation. This represents a natural Tier 1 progression.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 37. CoQ10 Stacking with Statins ---
  {
    id: 'safety-info-coq10-statin',
    name: 'Statin + CoQ10 Stacking Recommendation',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-rosuvastatin'],
    affectedProductIds: ['rx-rosuvastatin'],
    message: 'Statins deplete CoQ10 (shared mevalonate pathway). Adding CoQ10 200mg daily may reduce statin myalgia risk and support mitochondrial function. Consider including in the protocol.',
    clinicalNote: 'HMG-CoA reductase inhibition reduces mevalonate, the shared precursor for both cholesterol and CoQ10 (ubiquinone). Statin-induced CoQ10 depletion may contribute to myalgia (reported in 10-25% of statin users). While RCT evidence for CoQ10 preventing statin myopathy is mixed, the biological rationale is strong and supplementation is low-risk. If CoQ10 is not already in the protocol, suggest adding it. This is also a revenue-positive addition.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 38. Tier 1 to Tier 2 Escalation Suggestion ---
  {
    id: 'safety-info-tier-escalation',
    name: 'Tier 1 Insufficient — Consider Tier 2 Escalation',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['supp-berberine', 'supp-chromium-picolinate'],
    affectedProductIds: [],
    message: 'If metabolic markers have not improved after 90 days on the Metabolic Reset bundle, consider escalation to Metabolic Rx (Tier 2) with metformin ER for stronger AMPK activation.',
    clinicalNote: 'Standard protocol: 90-day trial of Tier 1 (OTC) before considering Tier 2 (Rx) escalation. If A1C remains >5.5, fasting insulin >10, or HOMA-IR >2.0 after 90 days of Metabolic Reset, escalate to Metabolic Rx bundle. This requires a $49 provider visit for prescription. Berberine can be continued at reduced dose (500mg/day) alongside metformin, or discontinued depending on provider preference.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 39. Magnesium Form Optimization ---
  {
    id: 'safety-info-magnesium-form',
    name: 'Magnesium Form Selection — Optimization Note',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['supp-magnesium-glycinate'],
    affectedProductIds: ['supp-magnesium-glycinate'],
    message: 'Magnesium glycinate is recommended for sleep and relaxation (glycine is an inhibitory neurotransmitter). For cognitive focus, consider magnesium L-threonate (crosses BBB). For constipation, magnesium citrate/oxide.',
    clinicalNote: 'Different magnesium salts have different clinical applications: Glycinate — highest bioavailability, calming (glycine is an inhibitory NT), best for sleep/anxiety. L-Threonate — only form shown to increase brain Mg levels (Magtein), best for cognition. Citrate — moderate bioavailability, mild osmotic laxative effect. Oxide — lowest bioavailability, strongest laxative. Taurate — cardiovascular support (taurine + Mg synergy). Default protocol uses glycinate; adjust form based on patient goals.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 40. NAD+ + Rapamycin Synergy Note ---
  {
    id: 'safety-info-nad-rapamycin-synergy',
    name: 'NAD+ + Rapamycin Longevity Stack — Synergy Note',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['nad-subq', 'rapamycin'],
    affectedProductIds: ['nad-subq', 'rapamycin'],
    message: 'NAD+ and rapamycin target complementary longevity pathways (sirtuin activation and mTOR inhibition). This combination is the core of the Longevity Core bundle and represents the highest-evidence pharmacological longevity stack. Add metformin (AMPK) for the full triad.',
    clinicalNote: 'The three master longevity pathways — mTOR (rapamycin), AMPK (metformin), and sirtuins (NAD+) — are interconnected. mTOR inhibition and AMPK activation both promote autophagy. NAD+ is required for sirtuin enzymatic activity (deacetylases). Rapamycin + NAD+ + metformin represents the "longevity triad" targeting all three nodes. If patient is on NAD+ Cellular (Tier 3 entry), suggest upgrading to Longevity Core for the full triad at $599/mo.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 41. Omega-3 + Curcumin Anti-Inflammatory Synergy ---
  {
    id: 'safety-info-omega3-curcumin-synergy',
    name: 'Omega-3 + Curcumin — Anti-Inflammatory Synergy',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['supp-omega-3-fish-oil', 'supp-curcumin-turmeric'],
    affectedProductIds: ['supp-omega-3-fish-oil', 'supp-curcumin-turmeric'],
    message: 'Omega-3 (EPA/DHA) and curcumin provide synergistic anti-inflammatory effects through complementary pathways (COX/LOX + NF-kB inhibition). Excellent combination for hs-CRP reduction.',
    clinicalNote: 'EPA/DHA are substrates for anti-inflammatory resolvins and protectins via the COX/LOX pathways. Curcumin inhibits NF-kB, the master inflammatory transcription factor. Together, they address both upstream (NF-kB) and downstream (eicosanoid) inflammatory cascades. Clinical studies show the combination reduces hs-CRP more effectively than either alone. Both are in the Inflammation Control bundle by default.',
    isActive: true,
    updatedAt: '2026-02-12',
  },

  // --- 42. Semax + Selank Cognitive Stack Note ---
  {
    id: 'safety-info-semax-selank-stack',
    name: 'Semax + Selank — Complementary Nootropic Stack',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['semax', 'selank'],
    affectedProductIds: ['semax', 'selank'],
    message: 'Semax (BDNF upregulation, stimulating) and Selank (anxiolytic, calming) are complementary Russian neuropeptides frequently stacked for balanced cognitive enhancement without anxiety or overstimulation.',
    clinicalNote: 'Semax and Selank were developed at the same Russian research institute and are designed as complementary agents. Semax provides ACTH-fragment-mediated cognitive stimulation and BDNF upregulation; Selank provides tuftsin-analog-mediated anxiolysis and immune modulation. The combination provides "calm focus" — cognitive enhancement without the anxiety that sometimes accompanies nootropics. Both are administered intranasally with compatible protocols. Common stack in Russian clinical practice.',
    isActive: true,
    updatedAt: '2026-02-12',
  },
];
